import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  cycle_no: string;
  title: string;
  status: string;
  started_on: string;
  session_name?: string;
  session_code?: string;
  framework_code?: string;
  version_label?: string;
  item_count?: number;
  sip_count?: number;
};

export default function EqamsListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.eqams.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-eqams-cycles', status, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/eqams/cycles', {
        params: { status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('eqams')}
      description={t('eqamsIntro')}
      extra={
        canManage ? (
          <Button type="primary" onClick={() => navigate('/education/eqams/new')}>
            {t('newEqamsCycle')}
          </Button>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 160 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'draft', label: t('statusDraft') },
                  { value: 'in_progress', label: t('statusInProgress') },
                  { value: 'closed', label: t('statusClosed') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/eqams/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('cycleNo'), dataIndex: 'cycle_no', width: 160 },
              { title: t('title'), dataIndex: 'title' },
              {
                title: t('session'),
                render: (_: unknown, row: Row) => row.session_name || row.session_code || '—',
              },
              {
                title: t('frameworkCode'),
                render: (_: unknown, row: Row) =>
                  row.framework_code
                    ? `${row.framework_code}${row.version_label ? ` / ${row.version_label}` : ''}`
                    : '—',
              },
              { title: t('startedOn'), dataIndex: 'started_on', width: 120 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
